// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// The drill-down every view shares. Clicking a station anywhere on the site opens this, so a
// click never merely selects something — it always leads somewhere.

import { openOverlay } from '../overlay';
import { loadDaily, loadEpisodes } from '../data';
import { el, svg } from '../components/ui';
import { concentration, dayNumberToYmd, number, percent, prettyDate, prettyHour, tip, UNIT } from '../format';
import { bandColour, EPISODE_TYPE_COLOUR } from '../palette';
import { setState } from '../state';
import type { Station, StationYear } from '../types';

const NEPM_DAILY = { 'pm2.5': 25, pm10: 50 } as const;
const WHO_DAILY = { 'pm2.5': 15, pm10: 45 } as const;

export function openStationYear(row: StationYear): void {
  openStation(
    { id: row.clusterId, name: row.name, jurisdiction: row.jurisdiction } as Station,
    row.year
  );
}

export async function openStation(station: Station, focusYear?: number): Promise<void> {
  const { body } = openOverlay({
    variant: 'drawer',
    title: `${station.name} (${station.jurisdiction})`,
    onClose: () => undefined,
  });

  body.innerHTML = '<p>Reading this station’s daily record…</p>';

  const [daily, episodes] = await Promise.all([loadDaily(station.id), loadEpisodes()]);
  body.innerHTML = '';

  if (!daily || !daily['pm2.5']) {
    body.append(el('p', '', 'No daily particle record is published for this station.'));
    return;
  }

  const series = daily['pm2.5']!;
  const values = series.values;
  const first = values[0];
  const last = values[values.length - 1];

  const nepmDays = values.filter(([, value]) => value > NEPM_DAILY['pm2.5']).length;
  const whoDays = values.filter(([, value]) => value > WHO_DAILY['pm2.5']).length;
  const worst = values.reduce((best, entry) => (entry[1] > best[1] ? entry : best), values[0]);

  const meta = el('div', 'detail-meta');
  const add = (label: string, value: string, tipText?: string) => {
    const cell = el('div');
    if (tipText) cell.setAttribute('data-tip', tipText);
    cell.append(el('div', 'stat-label', label));
    cell.append(el('div', 'stat-value', value));
    meta.append(cell);
  };
  add('Daily record', `${prettyDate(dayNumberToYmd(first[0]))} → ${prettyDate(dayNumberToYmd(last[0]))}`);
  add('Days measured', number(values.length));
  add(
    'Days over the AU standard',
    number(nepmDays),
    tip('Daily PM2.5 above 25 µg/m³, as measured.', 'No day has been set aside as an exceptional event.')
  );
  add(
    'Days over the WHO guideline',
    number(whoDays),
    tip('Daily PM2.5 above 15 µg/m³ — the World Health Organization’s 2021 level.')
  );
  add('Worst day', `${concentration(worst[1])} ${UNIT}`, tip(prettyDate(dayNumberToYmd(worst[0]))));
  body.append(meta);

  body.append(el('h3', '', 'Daily PM2.5, whole record'));
  body.append(
    el(
      'p',
      '',
      `Each vertical mark is one day, coloured on the published category scale. ${
        series.source === 'published 24-hour average'
          ? 'These are the agency’s own published 24-hour averages.'
          : 'These are 24-hour means computed here from the hourly record, requiring at least 18 valid hours in a day.'
      }`
    )
  );

  const width = 460;
  const height = 130;
  const chart = svg('svg', {
    viewBox: `0 0 ${width} ${height}`,
    class: 'chart',
    role: 'img',
    'aria-label': `Daily PM2.5 at ${station.name}`,
  });
  chart.style.width = '100%';
  chart.style.height = 'auto';
  const minDay = first[0];
  const maxDay = last[0];
  const spanDays = Math.max(1, maxDay - minDay);
  const maxValue = Math.max(50, ...values.map(([, value]) => value));
  const scale = (value: number) => height - 14 - (Math.sqrt(value) / Math.sqrt(maxValue)) * (height - 24);

  chart.append(svg('rect', { x: 0, y: 0, width, height, fill: 'var(--bg-elevated)', rx: 4 }));
  // The standard, drawn as a rule so every mark above it is legible as an exceedance.
  const standardY = scale(NEPM_DAILY['pm2.5']);
  chart.append(
    svg('line', {
      x1: 0, y1: standardY, x2: width, y2: standardY,
      stroke: 'var(--text-muted)', 'stroke-width': 1, 'stroke-dasharray': '4 3',
    })
  );
  const label = svg('text', { x: 4, y: standardY - 3, fill: 'var(--text-tertiary)', 'font-size': 8 });
  label.textContent = `25 ${UNIT} — Australian daily standard`;
  chart.append(label);

  for (const [day, value] of values) {
    const x = ((day - minDay) / spanDays) * (width - 2) + 1;
    const line = svg('line', {
      x1: x, y1: height - 14, x2: x, y2: scale(value),
      stroke: bandColour(value),
      'stroke-width': Math.max(0.5, (width - 2) / spanDays),
    });
    line.setAttribute(
      'data-tip',
      tip(`${prettyDate(dayNumberToYmd(day))}`, `${concentration(value)} ${UNIT} daily average`)
    );
    chart.append(line);
  }
  for (const year of [first, last].map(([day]) => Number(dayNumberToYmd(day).slice(0, 4)))) {
    const text = svg('text', {
      x: year === Number(dayNumberToYmd(first[0]).slice(0, 4)) ? 2 : width - 24,
      y: height - 3,
      fill: 'var(--text-tertiary)',
      'font-size': 9,
    });
    text.textContent = String(year);
    chart.append(text);
  }
  body.append(chart);

  const stationEpisodes = episodes
    .filter((episode) => episode.members.some((member) => member.clusterId === station.id))
    .sort((a, b) => b.peakValue - a.peakValue)
    .slice(0, 8);

  if (stationEpisodes.length) {
    body.append(el('h3', '', 'Worst episodes recorded here'));
    const list = el('div');
    for (const episode of stationEpisodes) {
      const row = el('button', 'bar-row');
      row.type = 'button';
      row.setAttribute(
        'data-tip',
        tip(
          `${episode.type}${episode.suspect ? ' (suspect — one cluster only)' : ''}`,
          `Peak ${concentration(episode.peakValue)} ${UNIT} at ${prettyHour(episode.peakLocal)}`,
          `${episode.clusterCount} clusters affected, ${episode.durationHours} hours`
        )
      );
      const name = el('span', 'bar-label');
      name.textContent = `${prettyHour(episode.peakLocal)} — ${episode.type}`;
      const track = el('span', 'bar-track');
      const fill = el('span', 'bar-fill');
      fill.style.width = `${Math.min(100, (episode.peakValue / stationEpisodes[0].peakValue) * 100)}%`;
      fill.style.background = EPISODE_TYPE_COLOUR[episode.type] ?? 'var(--accent-primary)';
      track.append(fill);
      const value = el('span', 'bar-value');
      value.textContent = concentration(episode.peakValue);
      row.append(name, track, value);
      row.addEventListener('click', () => {
        setState({ episode: episode.id, station: station.id, view: 'map' });
      });
      list.append(row);
    }
    body.append(list);
  }

  const actions = el('div', 'control-row');
  for (const [label, view] of [
    ['See this station’s clock', 'clock'],
    ['See where its bad air blows from', 'rose'],
    ['Find it on the map', 'map'],
  ] as const) {
    const button = el('button', 'button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', () => setState({ station: station.id, view }));
    actions.append(button);
  }
  body.append(actions);

  if (focusYear) {
    body.append(
      el(
        'p',
        'note',
        `Opened from ${focusYear}. That year contributed ${percent(
          values.filter(([day]) => dayNumberToYmd(day).startsWith(String(focusYear))).length / 365
        )} of a full year of daily readings here.`
      )
    );
  }
}
